import config, { Country, Language, Locale } from 'config';
import i18n, { ResourceLanguage } from 'i18next';
import jsCookie from 'js-cookie';
import { initReactI18next } from 'react-i18next';

import TranslationService from '../services/translation.service';
import store from '../store';
import { storeLocale } from '../store/actions/common.actions';
import { getAvailableLanguagesForCountry, getDefaultLanguageForCountry, isValidCountry, isValidLanguage } from './country-language-mapping';
import formatDate from './format-date';
import { i18nKeyModePostProcessor, isKeyModeEnabled } from './i18nKeyMode';
import { isNil } from './is-nil';

jsCookie.defaults = {
  sameSite: 'Strict',
  expires: config.get('web.cookieExpiry'),
  path: '/',
};

let pollingInitialized = false;

const env = config.get('env');
const isDevEnv = ['dev', 'local'].includes(env);

export const STORAGE_LOCALE_KEY = 'locale';

export const storeLocaleToCookie = (locale: Locale) => jsCookie.set(STORAGE_LOCALE_KEY, locale);

/**
 * Init i18n translation system with support for dynamic user country
 * @param userLocale - User's preferred locale (from cookies/storage)
 * @param userCountry - User's current country (from user profile, optional)
 */
export function initI18n(userLocale?: Locale | null, userCountry?: Country | null) {
  // Use deployment country as fallback
  const deploymentCountryLanguage = config.get('countryLanguage') as Language;
  const deploymentCountry = config.get('country') as Country;

  // Determine which country to use for language availability
  const effectiveCountry = userCountry && isValidCountry(userCountry) ? userCountry : deploymentCountry;
  const availableLanguages = getAvailableLanguagesForCountry(effectiveCountry);
  const defaultLanguageForCountry = getDefaultLanguageForCountry(effectiveCountry);

  // Extract language and country from userLocale
  const [userLanguage, userLocaleCountry] = userLocale?.split?.('-') ?? [];

  // Determine locale to initialize
  let localeToInit: Locale;

  if (userLanguage && isValidLanguage(userLanguage) && availableLanguages.includes(userLanguage)) {
    // User's language is valid and available for their country
    localeToInit = `${userLanguage}-${effectiveCountry}` as Locale;
  } else if (userLanguage && isValidLanguage(userLanguage) && userLocaleCountry && isValidCountry(userLocaleCountry)) {
    // User has a stored locale, but language might not be available for current country
    // Check if we should fall back to country default
    localeToInit = `${defaultLanguageForCountry}-${effectiveCountry}` as Locale;
  } else {
    // Default fallback
    localeToInit = `${defaultLanguageForCountry}-${effectiveCountry}` as Locale;
  }

  const fallbackLocale = `${deploymentCountryLanguage}-${deploymentCountry}` as Locale;

  // Init i18n
  i18n
    .use(initReactI18next)
    .use(i18nKeyModePostProcessor)
    .init({
      resources: getI18nResources(),
      lng: localeToInit,
      fallbackLng: fallbackLocale,
      debug: config.get('debug'),
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
        skipOnVariables: false,
      },
      postProcess: ['keyMode'], // Enable the keyMode postProcessor
      returnNull: !isKeyModeEnabled(),
      returnEmptyString: !isKeyModeEnabled(),
    });

  const initializedLocale = i18n.language as Locale;

  setLocale(initializedLocale);

  return initializedLocale;
}

export function setLocale(locale: Locale) {
  storeLocaleToCookie(locale);
  storeLocale(locale);
}

export function getLocale() {
  const currentLocale = store.getState()?.commonData?.locale;

  return currentLocale as Locale;
}

export function getLanguage() {
  const currentLocale = getLocale();
  const language = currentLocale?.split?.('-')?.[0] as Language;

  return language;
}

export function getCountry(): Country {
  const currentLocale = getLocale();
  const country = currentLocale?.split?.('-')?.[1] as Country;

  return country;
}

/** Get resources object for useSSR */
export function getI18nResources() {
  const translationFiles = config.get('translationFiles');

  return translationFiles.reduce((translationMap, translationFile) => {
    translationMap[translationFile.languageCode] = {};
    try {
      // eslint-disable-next-line global-require, import/no-dynamic-require
      translationMap[translationFile.languageCode].translation = require(`../translations/${translationFile.filePath}`);
    } catch (err) {
      console.warn(`Loading "${translationFile.languageCode}" translation failed:`, err.message);
    }

    return translationMap;
  }, {});
}

function replaceTranslations(locale: Locale, newTranslations: ResourceLanguage, fullReplace = false) {
  // Remove old translations for this locale
  if (fullReplace && i18n.hasResourceBundle(locale, 'translation')) {
    i18n.removeResourceBundle(locale, 'translation');
  }

  // Add the new bundle
  i18n.addResourceBundle(locale, 'translation', newTranslations, true, true);

  // If the active language is the one we just replaced, refresh it
  if (i18n.language === locale) {
    i18n.changeLanguage(locale);
  }
}

export function pollForTranslationUpdates() {
  const translationService = new TranslationService();

  // eslint-disable-next-line no-console
  const log = (...args) => (isDevEnv ? console.log(...args) : null);

  if (!config.translations.pollEnabled) {
    if (!pollingInitialized) {
      log('⚠️ Translations polling disabled');
      pollingInitialized = true;
    }

    return;
  }

  if (!pollingInitialized) {
    log(`✅ Translations polling enabled, interval: ${config.translations.pollTimeout}ms`);
    pollingInitialized = true;
  }

  const translationUpdates = async () => {
    try {
      const { translationsMap } = await translationService.getAllTranslations({ silentFail: true });
      const translationLocales = Object.keys(translationsMap ?? {});

      const updatedTargetLocales = [];
      const unsupportedTargetLocales = [];

      translationLocales.forEach((locale: Locale) => {
        if (i18n.hasResourceBundle(locale, 'translation')) {
          replaceTranslations(locale, translationsMap[locale]);
          updatedTargetLocales.push(locale);
        } else {
          unsupportedTargetLocales.push(locale);
          this.log('warn', `Translations for "${locale}" not updated, locale is not supported.`);
        }
      });

      log(
        `Update translations (${formatDate(new Date(), true)}): ` +
          [
            updatedTargetLocales.length ? `updated translations for ${updatedTargetLocales.join(', ')}` : null,
            unsupportedTargetLocales.length
              ? `unsupported locales found translation service provider: ${unsupportedTargetLocales.join(', ')}`
              : null,
          ]
            .filter((infoLine) => !isNil(infoLine))
            .join(', '),
      );
    } catch (err) {
      log('❌ Failed to update translations', err.message);
    }
  };

  const pollIntervalRef = setInterval(translationUpdates, config.translations.pollTimeout);

  // Run first immediately.
  translationUpdates();

  return pollIntervalRef;
}

export default i18n;
